
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Camera, Save, Store, ExternalLink, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEtsyConnection } from "@/hooks/useEtsyConnection";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useEtsyContext } from "@/contexts/EtsyContext";
import DashboardLayout from "@/components/DashboardLayout";

interface UserProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface EtsyShop {
  id: string;
  shop_id: number;
  shop_name: string;
  shop_url: string | null;
  created_at: string;
}

const Settings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { hasActiveSubscription } = useSubscriptionStatus();
  const { connectEtsy, isConnecting } = useEtsyConnection(hasActiveSubscription);
  const { selectedStoreIds, toggleStoreSelection } = useEtsyContext();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [etsyShops, setEtsyShops] = useState<EtsyShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingShops, setLoadingShops] = useState(false);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  // Notification preferences (all disabled)
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [newOrderAlerts, setNewOrderAlerts] = useState(false);
  const [lowStockWarnings, setLowStockWarnings] = useState(false);
  const [weeklyReports, setWeeklyReports] = useState(false);
  const [marketingUpdates, setMarketingUpdates] = useState(false);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchEtsyShops();
    }
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
        toast({
          title: "Error",
          description: "Failed to load profile data.",
          variant: "destructive",
        });
        return;
      }

      setProfile(data);
      
      // Set form values from profile data or user metadata
      const fullName = data?.full_name || user.user_metadata?.full_name || "";
      const nameParts = fullName.split(" ");
      setFirstName(nameParts[0] || "");
      setLastName(nameParts.slice(1).join(" ") || "");
      setEmail(data?.email || user.email || "");
      
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast({
        title: "Error",
        description: "Failed to load profile data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEtsyShops = async () => {
    if (!user) return;

    try {
      setLoadingShops(true);
      const { data, error } = await supabase
        .from('etsy_shops')
        .select('id, shop_id, shop_name, shop_url, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching Etsy shops:', error);
        toast({
          title: "Error",
          description: "Failed to load Etsy shops.",
          variant: "destructive",
        });
        return;
      }

      const shops = data || [];
      setEtsyShops(shops);

      // If there are no selected stores but there are shops, select all as default
      if (selectedStoreIds.length === 0 && shops.length > 0) {
        shops.forEach(shop => toggleStoreSelection(shop.id));
      }
    } catch (error) {
      console.error('Error fetching Etsy shops:', error);
      toast({
        title: "Error",
        description: "Failed to load Etsy shops.",
        variant: "destructive",
      });
    } finally {
      setLoadingShops(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    try {
      setSaving(true);
      const fullName = `${firstName} ${lastName}`.trim();

      // Update or insert profile
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          full_name: fullName,
          email: email,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;

      // Update local state
      setProfile(prev => ({
        ...prev,
        id: user.id,
        full_name: fullName,
        email: email,
      }));

      toast({
        title: "Success",
        description: "Profile updated successfully.",
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStoreToggle = (storeId: string) => {
    toggleStoreSelection(storeId);
    toast({
      title: "Store Selection Updated",
      description: "Store selection has been updated. Data will be refreshed.",
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getDisplayName = () => {
    if (profile?.full_name) {
      return profile.full_name;
    }
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    return user?.email?.split('@')[0] || 'User';
  };

  const getAvatarFallback = () => {
    const displayName = getDisplayName();
    if (displayName && displayName !== 'User') {
      return getInitials(displayName);
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading settings...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">Manage your account settings and preferences.</p>
        </div>

        {/* Profile Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Update your personal information and profile details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center space-x-4">
              <Avatar className="w-20 h-20">
                <AvatarImage 
                  src={profile?.avatar_url || user?.user_metadata?.avatar_url} 
                  alt="Profile" 
                />
                <AvatarFallback>{getAvatarFallback()}</AvatarFallback>
              </Avatar>
              <Button variant="outline">
                <Camera className="mr-2 h-4 w-4" />
                Change Photo
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input 
                  id="firstName" 
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input 
                  id="lastName" 
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input 
                id="email" 
                type="email" 
                value={email}
                readOnly
                disabled
                className="bg-gray-100 cursor-not-allowed"
              />
              <p className="text-sm text-gray-500">Email cannot be changed for security reasons.</p>
            </div>
            
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSaveProfile}
              disabled={saving}
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </CardContent>
        </Card>

        {/* Integrated Stores */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Store className="h-5 w-5" />
                  Integrated Stores
                </CardTitle>
                <CardDescription>
                  Manage your connected Etsy stores. Select which stores to include in your dashboard data.
                </CardDescription>
              </div>
              <Button 
                onClick={connectEtsy}
                disabled={isConnecting || !hasActiveSubscription}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                {isConnecting ? 'Connecting...' : 'Add Etsy Store'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!hasActiveSubscription && (
              <div className="text-center py-4 mb-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800">
                  You need an active subscription to connect your Etsy store.
                </p>
              </div>
            )}
            {loadingShops ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : etsyShops.length === 0 ? (
              <div className="text-center py-8">
                <Store className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No stores connected</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Connect your Etsy store to start managing your orders and products.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Active</TableHead>
                      <TableHead>Shop Name</TableHead>
                      <TableHead>Shop ID</TableHead>
                      <TableHead>Connected Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {etsyShops.map((shop) => (
                      <TableRow key={shop.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedStoreIds.includes(shop.id)}
                            onCheckedChange={() => handleStoreToggle(shop.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {shop.shop_name}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {shop.shop_id}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {formatDate(shop.created_at)}
                        </TableCell>
                        <TableCell>
                          {shop.shop_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(shop.shop_url!, '_blank')}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              View Store
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
            <CardDescription>Configure how you receive notifications. <span className="text-purple-600 font-medium">Coming Soon</span></CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between opacity-50">
              <div className="space-y-0.5">
                <Label>Email Notifications</Label>
                <p className="text-sm text-gray-600">Receive notifications via email</p>
              </div>
              <Switch 
                checked={emailNotifications}
                onCheckedChange={setEmailNotifications}
                disabled
              />
            </div>
            
            <div className="flex items-center justify-between opacity-50">
              <div className="space-y-0.5">
                <Label>New Order Alerts</Label>
                <p className="text-sm text-gray-600">Get notified when you receive new orders</p>
              </div>
              <Switch 
                checked={newOrderAlerts}
                onCheckedChange={setNewOrderAlerts}
                disabled
              />
            </div>
            
            <div className="flex items-center justify-between opacity-50">
              <div className="space-y-0.5">
                <Label>Low Stock Warnings</Label>
                <p className="text-sm text-gray-600">Alert when products are running low</p>
              </div>
              <Switch 
                checked={lowStockWarnings}
                onCheckedChange={setLowStockWarnings}
                disabled
              />
            </div>
            
            <div className="flex items-center justify-between opacity-50">
              <div className="space-y-0.5">
                <Label>Weekly Reports</Label>
                <p className="text-sm text-gray-600">Receive weekly performance summaries</p>
              </div>
              <Switch 
                checked={weeklyReports}
                onCheckedChange={setWeeklyReports}
                disabled
              />
            </div>
            
            <div className="flex items-center justify-between opacity-50">
              <div className="space-y-0.5">
                <Label>Marketing Updates</Label>
                <p className="text-sm text-gray-600">Tips and updates about growing your business</p>
              </div>
              <Switch 
                checked={marketingUpdates}
                onCheckedChange={setMarketingUpdates}
                disabled
              />
            </div>
            
            <div className="text-center py-4">
              <p className="text-purple-600 font-medium">Coming Soon</p>
              <p className="text-sm text-gray-500 mt-1">Notification preferences will be available in a future update.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
